import { useEffect, useState } from 'react';
import { clearIdentity, fetchMe, getEmail, updateMe } from '../api';
import type { Me } from '../types';
import AdminSheet from './AdminSheet';

export default function MeSheet({
  onClose,
  onSignOut,
}: {
  onClose: () => void;
  onSignOut: () => void;
}) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Check if user has admin access
  const hasAdminAccess = me?.role === 'super_admin' || me?.role === 'admin';

  useEffect(() => {
    fetchMe()
      .then((d) => {
        setMe(d.user);
        setName(d.user?.name ?? '');
        setPhone(d.user?.phone ?? '');
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'load failed'));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const { user: updated } = await updateMe({ name, phone });
      setMe(updated);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-x-0 bottom-0 max-h-[70vh] overflow-hidden rounded-t-3xl bg-white p-5 shadow-2xl"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">My profile</h2>
          <button onClick={onClose} className="text-sm text-slate-500">
            Close
          </button>
        </div>

        {err && <p className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">Email</label>
            <div className="mt-1 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">
              {me?.email ?? getEmail()}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Satish R."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600">Phone (E.164)</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+9198…"
              inputMode="tel"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full rounded-lg bg-wechat-accent px-3 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          {hasAdminAccess && (
            <button
              onClick={() => setAdminOpen(true)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700"
            >
              Admin · Meta pages
            </button>
          )}

          <button
            onClick={() => {
              if (confirm('Sign out and clear local session?')) {
                clearIdentity();
                
                // Use the new authentication system logout
                onSignOut();
              }
            }}
            className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700"
          >
            Sign out
          </button>
        </div>
      </div>
      {adminOpen && <AdminSheet onClose={() => setAdminOpen(false)} />}
    </div>
  );
}
